import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import { ErrorBoundary } from '@components/ErrorBoundary';
import {
  bootstrapGoogleAnalyticsWithConsentMode,
  setRuntimeGaMeasurementId,
} from '@utils/analytics';
import '@cloudscape-design/global-styles/index.css';

function parseRuntimeConfigJson(data: unknown): string | undefined {
  if (
    typeof data === 'object' &&
    data !== null &&
    'gaMeasurementId' in data &&
    typeof (data as { gaMeasurementId: unknown }).gaMeasurementId === 'string'
  ) {
    const id = (data as { gaMeasurementId: string }).gaMeasurementId.trim();
    return id === '' ? undefined : id;
  }
  return undefined;
}

async function startApp(): Promise<void> {
  let gaId = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim() ?? '';

  if (!gaId) {
    try {
      const res = await fetch('/runtime-config.json', { cache: 'no-store' });
      if (res.ok) {
        const parsed = parseRuntimeConfigJson(await res.json());
        if (parsed) {
          gaId = parsed;
        }
      }
    } catch {
      /* missing file, offline, or non-JSON */
    }
  }

  if (gaId) {
    // Keep module state in sync whether the ID came from Vite env or runtime-config.json.
    setRuntimeGaMeasurementId(gaId);
    bootstrapGoogleAnalyticsWithConsentMode(gaId);
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

void startApp();
